/**
 * Upload-and-review panel. Dropzone, file-selected indicator, the
 * verification summary pills, the review table, and the publish callout.
 *
 * Pure-presentational: every piece of state, every callback, and the
 * Apollo refetch handles live in the parent (`ImportPetsModal`). The
 * component is the largest sub-view; isolating it keeps the orchestrator
 * focused on flow + mutation wiring.
 */

import React, { type RefObject, useState } from 'react';
import Button from '../../../components/Button/Button';
import { ModalActions } from '../../../components/Modal/Modal';
import { formatBytes, MAX_FILE_BYTES, MAX_ROWS } from './constants';
import { CheckIcon, CloudUploadIcon, DownloadIcon, ErrorCircleIcon, FileIcon, MailIcon, StackIcon } from './icons';
import ReviewTable from './ReviewTable';
import type { ParsedRow } from './validateRow';
import styles from '../Integrations.module.scss';

interface UploadAndReviewProps {
  fileInputRef: RefObject<HTMLInputElement>;
  fileName: string | null;
  fileSizeBytes: number | null;

  isDragging: boolean;
  setIsDragging: (b: boolean) => void;
  handleFile: (f: File) => void;
  handleDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onReplaceFile: () => void;

  hasParsed: boolean;
  parseError: string | null;
  submitError: string | null;

  breedsBroken: boolean;
  refetchBreeds: () => Promise<unknown>;

  /** True when the chip-eligibility bulk lookup failed (network/GraphQL). */
  eligibilityBroken: boolean;
  /** Caller-supplied retry for the chip-eligibility lookup. */
  refetchEligibility: () => void;

  verifiedRows: ParsedRow[];
  displayedRows: ParsedRow[];
  validRowCount: number;
  invalidRowCount: number;
  duplicateRowCount: number;
  claimRowCount: number;

  verifying: boolean;
  importing: boolean;
  publishDisabled: boolean;
  publishLabel: string;

  filterMode: 'all' | 'errors';
  setFilterMode: (m: 'all' | 'errors') => void;

  onBack: () => void;
  onPublish: () => void;
}

export default function UploadAndReview({
  fileInputRef,
  fileName,
  fileSizeBytes,
  isDragging,
  setIsDragging,
  handleFile,
  handleDrop,
  onReplaceFile,
  hasParsed,
  parseError,
  submitError,
  breedsBroken,
  refetchBreeds,
  eligibilityBroken,
  refetchEligibility,
  verifiedRows,
  displayedRows,
  validRowCount,
  invalidRowCount,
  duplicateRowCount,
  claimRowCount,
  verifying,
  importing,
  publishDisabled,
  publishLabel,
  filterMode,
  setFilterMode,
  onBack,
  onPublish,
}: UploadAndReviewProps) {
  function openFilePicker() {
    fileInputRef.current?.click();
  }

  // Separate drag state for the file-selected card so its visual cue
  // doesn't fight with the upload-step dropzone's `isDragging` (the
  // two are never on screen at the same time, but keeping the state
  // independent makes the handlers self-contained).
  const [replaceDragging, setReplaceDragging] = useState(false);
  function handleReplaceDragOver(e: React.DragEvent<HTMLDivElement>) {
    if (importing) {
      return;
    }
    e.preventDefault();
    setReplaceDragging(true);
  }
  function handleReplaceDragLeave() {
    setReplaceDragging(false);
  }
  function handleReplaceDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setReplaceDragging(false);
    if (importing) {
      return;
    }
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFile(file);
    }
  }

  return (
    <div className={`${styles.wizardStep} ${styles.uploadStep}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            handleFile(file);
          }
          e.target.value = '';
        }}
      />

      {!hasParsed && (
        <div
          className={`${styles.dropzone} ${isDragging ? styles.dropzoneActive : ''}`}
          onClick={openFilePicker}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openFilePicker();
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          role="button"
          tabIndex={0}
        >
          <div className={styles.dropzoneIcon}>
            <CloudUploadIcon />
          </div>
          <h4 className={styles.dropzoneTitle}>Drop your CSV here</h4>
          <p className={styles.dropzoneHint}>
            or <span className={styles.dropzoneBrowse}>click to browse</span> · Up to {MAX_ROWS} chips ·{' '}
            {formatBytes(MAX_FILE_BYTES)} max
          </p>
          {fileName && <div className={styles.uploadFilename}>{fileName}</div>}
          {parseError && <div className={styles.fieldError}>{parseError}</div>}
        </div>
      )}

      {hasParsed && (
        <>
          <div
            className={`${styles.fileSelected} ${replaceDragging ? styles.fileSelectedActive : ''}`}
            onDragOver={handleReplaceDragOver}
            onDragLeave={handleReplaceDragLeave}
            onDrop={handleReplaceDrop}
          >
            <div className={styles.fileSelectedIcon}>
              <FileIcon />
            </div>
            <div className={styles.fileSelectedMeta}>
              <div className={styles.fileSelectedName}>{fileName}</div>
              <div className={styles.fileSelectedInfo}>
                {replaceDragging ? (
                  <span className={styles.fileSelectedDropHint}>Drop to replace this file</span>
                ) : (
                  <>
                    {verifiedRows.length} row{verifiedRows.length === 1 ? '' : 's'}
                    {fileSizeBytes !== null && <> · {formatBytes(fileSizeBytes)}</>}
                    {' · '}
                    {verifying ? 'checking…' : 'checked'}
                  </>
                )}
              </div>
            </div>
            <button type="button" className={styles.replaceLink} onClick={onReplaceFile} disabled={importing}>
              Replace file
            </button>
          </div>

          {breedsBroken && (
            <div className={styles.fieldError}>
              Couldn&rsquo;t load the breed list — we&rsquo;d false-flag every row that has a breed.{' '}
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => {
                  refetchBreeds().catch(() => {
                    // Swallow — Apollo's `error` state on the next
                    // render reflects the retry outcome.
                  });
                }}
              >
                Retry
              </button>
            </div>
          )}

          {eligibilityBroken && (
            <div className={styles.fieldError}>
              Couldn&rsquo;t verify chip ownership — publishing without this check could let the backend reject rows
              mid-import.{' '}
              <button type="button" className={styles.linkButton} onClick={refetchEligibility}>
                Retry
              </button>
            </div>
          )}

          {submitError && <div className={styles.fieldError}>{submitError}</div>}

          <div className={styles.validationSummary}>
            <div className={`${styles.summaryPill} ${styles.summaryPillValid}`}>
              <CheckIcon />
              <span className={styles.summaryPillCount}>{validRowCount}</span>
              <span>ready to publish</span>
            </div>
            <div
              className={`${styles.summaryPill} ${invalidRowCount > 0 ? styles.summaryPillError : styles.summaryPillMuted}`}
            >
              <ErrorCircleIcon />
              <span className={styles.summaryPillCount}>{invalidRowCount}</span>
              <span>need fixing</span>
            </div>
            <div
              className={`${styles.summaryPill} ${duplicateRowCount > 0 ? styles.summaryPillDuplicate : styles.summaryPillMuted}`}
            >
              <StackIcon />
              <span className={styles.summaryPillCount}>{duplicateRowCount}</span>
              <span>duplicate{duplicateRowCount === 1 ? '' : 's'}</span>
            </div>
            <div
              className={`${styles.summaryPill} ${claimRowCount > 0 ? styles.summaryPillClaim : styles.summaryPillMuted}`}
              title="Chips that aren&rsquo;t yet in your inventory — they&rsquo;ll be registered to your rescue when you publish."
            >
              <DownloadIcon />
              <span className={styles.summaryPillCount}>{claimRowCount}</span>
              <span>to register</span>
            </div>

            <div className={styles.filterGroup}>
              <button
                type="button"
                className={`${styles.filterChip} ${filterMode === 'all' ? styles.filterChipActive : ''}`}
                onClick={() => setFilterMode('all')}
              >
                All {verifiedRows.length}
              </button>
              <button
                type="button"
                className={`${styles.filterChip} ${filterMode === 'errors' ? styles.filterChipActive : ''}`}
                onClick={() => setFilterMode('errors')}
                disabled={invalidRowCount === 0}
              >
                Errors only
              </button>
            </div>
          </div>

          <ReviewTable
            rows={displayedRows}
            emptyMessage={filterMode === 'errors' ? 'No errors — everything looks good.' : 'No rows to display.'}
          />

          {validRowCount > 0 && invalidRowCount === 0 && !verifying && (
            <div className={styles.requiredCallout}>
              <div className={styles.requiredCalloutIcon}>
                <MailIcon />
              </div>
              <div className={styles.requiredCalloutText}>
                <strong>Publishing sends emails.</strong> Each of the {validRowCount} registered owner
                {validRowCount === 1 ? '' : 's'} gets a confirmation that their pet was added to the Fi Nano Microchip
                registry.
                {claimRowCount > 0 && (
                  <>
                    {' '}
                    {claimRowCount} chip{claimRowCount === 1 ? '' : 's'} will be registered to your rescue at the same
                    time.
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <ModalActions>
        <Button variant="ghost" onClick={onBack} disabled={importing}>
          Back
        </Button>
        <Button variant="green" onClick={onPublish} disabled={publishDisabled}>
          {publishLabel}
        </Button>
      </ModalActions>
    </div>
  );
}
